import React from 'react';

/**
 * Pager: the one page control of the app, « ‹ Page 2 of 5 › ».
 *
 * Props:
 *   page        {number}   the page shown, from 1
 *   pages       {number}   how many pages there are
 *   onPage      {function} called with the page to show
 *   disabled    {boolean}  e.g. while loading
 *   compact     {boolean}  the small form for a panel header: « ‹ 2/5 › »
 *   hideSingle  {boolean}  show nothing when everything fits on one page
 *   total       {number}   optional: the row count, shown before the pager
 *   pageSize    {number}   optional, with onPageSize: a rows-per-page picker
 *   onPageSize  {function}
 *   pageSizes   {number[]}
 *   className   {string}
 */

const BTN = 'toolbar-control px-2 border border-gray-300 bg-white hover:bg-blue-100 transition disabled:opacity-50 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-600';
const LABEL = 'toolbar-control px-3 border border-gray-300 bg-blue-50 rounded text-blue-700 font-semibold whitespace-nowrap dark:bg-slate-700 dark:border-slate-600 dark:text-blue-300';
const COMPACT_BTN = 'px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300 hover:bg-white/70 dark:hover:bg-slate-700 disabled:opacity-40';

const Pager = ({
  page,
  pages,
  onPage,
  disabled = false,
  compact = false,
  hideSingle = false,
  total = null,
  pageSize = null,
  onPageSize = null,
  pageSizes = [10, 25, 50, 100],
  className = '',
}) => {
  const last = Math.max(1, Number(pages) || 1);
  const current = Math.min(last, Math.max(1, Number(page) || 1));
  if ((hideSingle || compact) && last <= 1) return null;

  const go = (p) => onPage(Math.min(last, Math.max(1, p)));
  const atStart = disabled || current <= 1;
  const atEnd = disabled || current >= last;
  const buttons = [
    { label: '«', title: 'First', to: 1, off: atStart },
    { label: '‹', title: 'Previous', to: current - 1, off: atStart },
    null,
    { label: '›', title: 'Next', to: current + 1, off: atEnd },
    { label: '»', title: 'Last', to: last, off: atEnd },
  ];

  if (compact) {
    return (
      <div className={`flex items-center gap-0.5 text-[11px] ${className}`}>
        {buttons.map((b) => (b ? (
          <button key={b.title} type="button" title={b.title} disabled={b.off} onClick={() => go(b.to)} className={COMPACT_BTN}>{b.label}</button>
        ) : (
          <span key="at" className="px-1 font-semibold text-slate-700 dark:text-slate-200">{current}/{last}</span>
        )))}
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {total !== null && (
        <span className="text-xs text-gray-600 dark:text-slate-400">{total} rows</span>
      )}
      {onPageSize && (
        <select
          value={pageSize ?? pageSizes[0]}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label="Rows per page"
          title="Rows per page"
          className="toolbar-control"
        >
          {pageSizes.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>
      )}
      <div className="flex items-center gap-1">
        {buttons.map((b, i) => (b ? (
          <button
            key={b.title}
            type="button"
            title={b.title}
            disabled={b.off}
            onClick={() => go(b.to)}
            className={`${BTN} ${i === 0 ? 'rounded-l-lg' : ''} ${i === buttons.length - 1 ? 'rounded-r-lg' : ''}`}
          >
            {b.label}
          </button>
        ) : (
          <span key="at" className={LABEL}>Page {current} of {last}</span>
        )))}
      </div>
    </div>
  );
};

export default Pager;
